import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

// Check if we're in test/local auth mode
const isTestEnvironment = process.env.REACT_APP_AUTH_MODE === 'local' ||
                          process.env.REACT_APP_ENVIRONMENT === 'test';

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading, user, logout } = useAuth();
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [checkingApproval, setCheckingApproval] = useState(true);

    useEffect(() => {
        const checkAndSyncUser = async () => {
            if (!isAuthenticated || !user) {
                setCheckingApproval(false);
                return;
            }

            // In test environment with local auth, skip approval check
            // Local auth users are automatically approved
            if (isTestEnvironment) {
                setApprovalStatus('approved');
                setCheckingApproval(false);
                return;
            }

            try {
                // Sync user with backend and get approval status
                const response = await fetch('/api/admin/users', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: user.sub,
                        email: user.email,
                        name: user.name,
                        picture: user.picture
                    })
                });

                const data = await response.json();

                if (response.status === 403) {
                    // Domain not allowed
                    setApprovalStatus('domain_denied');
                } else if (response.ok) {
                    setApprovalStatus(data.approval_status);
                } else {
                    console.error('Error syncing user:', data.error);
                    setApprovalStatus('error');
                }
            } catch (error) {
                console.error('Error checking approval:', error);
                setApprovalStatus('error');
            } finally {
                setCheckingApproval(false);
            }
        };

        if (!isLoading) {
            checkAndSyncUser();
        }
    }, [isAuthenticated, isLoading, user]);

    const handleLogout = () => {
        logout({
            logoutParams: {
                returnTo: window.location.origin + '/admin'
            }
        });
    };

    if (isLoading || checkingApproval) {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Loading...</h1>
                        <p>Checking authentication status</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/admin" replace />;
    }

    // In test environment, skip domain checks
    if (isTestEnvironment) {
        return children;
    }

    // Check if user email domain is @pcic.org.au or allowed test email
    const userEmail = user?.email || '';
    const allowedTestEmails = ['husainshah101@gmail.com'];
    const isAllowedDomain = userEmail.endsWith('@pcic.org.au');
    const isAllowedTestEmail = allowedTestEmails.includes(userEmail.toLowerCase());

    if ((!isAllowedDomain && !isAllowedTestEmail) || approvalStatus === 'domain_denied') {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Access Denied</h1>
                        <p>Only @pcic.org.au email addresses are allowed.</p>
                        <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            You signed in as: {userEmail}
                        </p>
                        <button
                            onClick={handleLogout}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                background: '#2A896F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (approvalStatus === 'pending') {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Pending Approval</h1>
                        <p>Your account is pending admin approval.</p>
                        <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            Please contact an existing administrator to approve your access.
                        </p>
                        <p style={{ marginTop: '10px', fontSize: '14px', color: '#888' }}>
                            Logged in as: {userEmail}
                        </p>
                        <button
                            onClick={handleLogout}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                background: '#2A896F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (approvalStatus === 'rejected') {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Access Denied</h1>
                        <p>Your access request has been rejected.</p>
                        <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                            Please contact an administrator if you believe this is an error.
                        </p>
                        <button
                            onClick={handleLogout}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                background: '#2A896F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (approvalStatus === 'error') {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Error</h1>
                        <p>Unable to verify your access. Please try again later.</p>
                        <button
                            onClick={handleLogout}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                background: '#2A896F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // User is approved
    return children;
};

export default ProtectedRoute;
